/**
 * Wheel of Fortune face, drawn once into a canvas inside `.retention-wheel-disc__face`.
 *
 * Cartoon prize wheel: each segment is a flat cel facet (its leading half lit, its trailing
 * half the base tone, a darker band at the rim for depth), thick ink spokes between segments,
 * an ink ring at the edge and a gold peg with a glint on every spoke. Segments run clockwise
 * from 12 o'clock like the CSS conic gradient it replaces, so the DOM labels
 * (`--wheel-label-angle`) and the spin maths stay aligned.
 */
import { INK, mixHex } from "../palette.js";
import { TAU, glint, inkStyle } from "../toon-kit.js";

/**
 * Saturated cartoon tones in the hue order of RETENTION_WHEEL_SEGMENT_COLORS
 * (red, pink, purple, violet, blue, green, yellow, orange).
 */
export var WHEEL_SEGMENT_TONES = [
  { base: "#ff6b6b", light: "#ff9b8f", shade: "#d9424c" },
  { base: "#ff8fc6", light: "#ffc0de", shade: "#d95f9e" },
  { base: "#b07cf0", light: "#d2b4ff", shade: "#8352cc" },
  { base: "#8a8cf5", light: "#bcbdff", shade: "#6062d0" },
  { base: "#45b4f0", light: "#9adcff", shade: "#2489c9" },
  { base: "#4fd189", light: "#9ff0bf", shade: "#2ba366" },
  { base: "#ffd04d", light: "#fff0a0", shade: "#e8a51c" },
  { base: "#ff9a45", light: "#ffc78a", shade: "#e06f22" },
];

/** Canvas size in device pixels (the face is shown at up to ~360 CSS px on 2× screens). */
var FACE_PX = 720;

/**
 * @param {HTMLElement | null | undefined} face `.retention-wheel-disc__face`
 * @param {number} count segment count
 * @returns {HTMLCanvasElement | null}
 */
export function paintWheelFace(face, count) {
  if (!face || typeof document === "undefined") return null;
  var cv = document.createElement("canvas");
  var c = null;
  try {
    c = cv.getContext && cv.getContext("2d");
  } catch (_e) {
    c = null;
  }
  if (!c) return null;
  cv.width = cv.height = FACE_PX;
  cv.className = "retention-wheel-disc__face-art";
  cv.setAttribute("aria-hidden", "true");
  drawWheelFace(c, FACE_PX / 2, Math.max(1, Math.floor(count) || 8));
  var old = face.querySelector(".retention-wheel-disc__face-art");
  if (old) old.remove();
  face.appendChild(cv);
  return cv;
}

/** Same tones as a conic gradient: the fallback face before (or without) the canvas. */
export function wheelFaceToonGradient(count) {
  var n = Math.max(1, Math.floor(count) || 8);
  var seg = 360 / n;
  var stops = [];
  for (var i = 0; i < n; i++) {
    var t = WHEEL_SEGMENT_TONES[i % WHEEL_SEGMENT_TONES.length];
    stops.push(t.base + " " + i * seg + "deg " + (i + 1) * seg + "deg");
  }
  return "conic-gradient(" + stops.join(", ") + ")";
}

/**
 * @param {CanvasRenderingContext2D} c
 * @param {number} R face radius (the canvas is 2R square)
 * @param {number} n segments
 */
export function drawWheelFace(c, R, n) {
  var seg = TAU / n;
  var ink = R * 0.022;
  c.save();
  c.translate(R, R);
  for (var i = 0; i < n; i++) {
    var tone = WHEEL_SEGMENT_TONES[i % WHEEL_SEGMENT_TONES.length];
    var a0 = -Math.PI / 2 + i * seg;
    var am = a0 + seg / 2;
    var a1 = a0 + seg;
    // Trailing half: base. Leading half: a lit facet.
    wedge(c, R, am, a1);
    c.fillStyle = tone.base;
    c.fill();
    wedge(c, R, a0, am);
    c.fillStyle = mixHex(tone.base, tone.light, 0.55);
    c.fill();
    // Rim band: the outer edge turns away from the light.
    c.beginPath();
    c.arc(0, 0, R, a0, a1);
    c.arc(0, 0, R * 0.88, a1, a0, true);
    c.closePath();
    c.fillStyle = tone.shade;
    c.fill();
    // A thin light line along the band's inner edge, like a bevel catching light.
    c.beginPath();
    c.arc(0, 0, R * 0.875, a0 + seg * 0.06, a1 - seg * 0.06);
    c.strokeStyle = mixHex(tone.light, "#ffffff", 0.4);
    c.lineWidth = R * 0.012;
    c.lineCap = "round";
    c.globalAlpha = 0.75;
    c.stroke();
    c.globalAlpha = 1;
    // Toy glint on the lit facet, near the rim.
    glint(c, Math.cos(a0 + seg * 0.3) * R * 0.74, Math.sin(a0 + seg * 0.3) * R * 0.74, R * 0.028);
  }
  // Ink spokes between segments.
  inkStyle(c, ink, INK);
  c.beginPath();
  for (var s = 0; s < n; s++) {
    var a = -Math.PI / 2 + s * seg;
    c.moveTo(0, 0);
    c.lineTo(Math.cos(a) * R, Math.sin(a) * R);
  }
  c.stroke();
  // Ink ring at the edge (half of it is under the CSS rim's inner edge).
  c.beginPath();
  c.arc(0, 0, R - ink * 0.5, 0, TAU);
  c.lineWidth = ink * 1.6;
  c.stroke();
  // Gold pegs on the spokes, just inside the rim.
  for (var p = 0; p < n; p++) {
    var pa = -Math.PI / 2 + p * seg;
    var px = Math.cos(pa) * R * 0.935, py = Math.sin(pa) * R * 0.935;
    var pr = R * 0.04;
    c.beginPath();
    c.arc(px, py, pr, 0, TAU);
    c.fillStyle = "#e8a51c";
    c.fill();
    c.beginPath();
    c.arc(px - pr * 0.18, py - pr * 0.22, pr * 0.78, 0, TAU);
    c.fillStyle = "#ffd04d";
    c.fill();
    c.beginPath();
    c.arc(px, py, pr, 0, TAU);
    inkStyle(c, ink * 0.75, INK);
    c.stroke();
    c.beginPath();
    c.arc(px - pr * 0.35, py - pr * 0.38, pr * 0.24, 0, TAU);
    c.fillStyle = "#ffffff";
    c.fill();
  }
  c.restore();
}

function wedge(c, R, a0, a1) {
  c.beginPath();
  c.moveTo(0, 0);
  c.arc(0, 0, R, a0, a1);
  c.closePath();
}
